import { useEffect } from 'react';
import { useEditorStore } from '@/store/editorStore';
import { useCollaborationStore } from '@/store/collaborationStore';
import type { CollaboratorAwareness } from './awareness';

/**
 * Syncs local editor selection/editing state into the Yjs awareness protocol,
 * and subscribes to remote awareness updates to populate the collaboration store.
 *
 * Call this hook once near the app root.
 */
export function useAwarenessSync(): void {
  const provider = useCollaborationStore((s) => s.provider);
  const setCollaborators = useCollaborationStore((s) => s.setCollaborators);
  const setConnected = useCollaborationStore((s) => s.setConnected);

  const selectedNodeIds = useEditorStore((s) => s.selectedNodeIds);
  const editingNodeId = useEditorStore((s) => s.editingNodeId);

  // Push local selection/editing state into awareness
  useEffect(() => {
    if (!provider) return;

    const selectedNodeId = selectedNodeIds[0] ?? null;
    provider.awareness.setLocalStateField('user', {
      ...((provider.awareness.getLocalState()?.['user'] as CollaboratorAwareness | undefined) ?? {
        userId: '',
        name: '',
        color: '#3b82f6',
        selectedNodeId: null,
        editingNodeId: null,
      }),
      selectedNodeId,
      editingNodeId,
    } satisfies CollaboratorAwareness);
  }, [provider, selectedNodeIds, editingNodeId]);

  // Subscribe to remote awareness changes and connection status
  useEffect(() => {
    if (!provider) return;

    const syncCollaborators = () => {
      const states = provider.awareness.getStates();
      const collabs = new Map<number, CollaboratorAwareness>();
      states.forEach((state, clientId) => {
        // Skip our own client
        if (clientId === provider.doc.clientID) return;
        const user = state['user'] as CollaboratorAwareness | undefined;
        if (user) {
          collabs.set(clientId, user);
        }
      });
      setCollaborators(collabs);
    };

    const onAwarenessChange = () => {
      syncCollaborators();
    };

    const onConnectionChange = () => {
      setConnected(provider.connected);
    };

    provider.awareness.on('change', onAwarenessChange);
    provider.onConnectionChange(onConnectionChange);

    // Initial sync
    syncCollaborators();
    setConnected(provider.connected);

    return () => {
      provider.awareness.off('change', onAwarenessChange);
    };
  }, [provider, setCollaborators, setConnected]);
}
